/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import {
  DEEP_LINK_ROUTE_MAP,
  PEAZY_APP_SCHEME,
  PEAZY_ANDROID_STORE_URL,
  PEAZY_IOS_STORE_URL,
  STORE_FALLBACK_DELAY_MS,
} from '../../../../Configuration/deepLinkRoutes';

type Platform = 'ios' | 'android' | 'other';

const detectPlatform = (): Platform => {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
};

const getStoreUrl = (platform: Platform): string | null => {
  if (platform === 'ios') return PEAZY_IOS_STORE_URL;
  if (platform === 'android') return PEAZY_ANDROID_STORE_URL;
  return null;
};

const buildAppUrl = (screen: string, id: string | null): string => {
  const route = DEEP_LINK_ROUTE_MAP[screen] ?? null;
  if (!route) return PEAZY_APP_SCHEME;
  const params = id ? `?id=${encodeURIComponent(id)}` : '';
  return `${PEAZY_APP_SCHEME}${route}${params}`;
};

const OpenDeepLink = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const [showStoreFallback, setShowStoreFallback] = useState(false);

  const screen = searchParams.get('screen') ?? '';
  const id = searchParams.get('id');
  const platform = detectPlatform();
  const storeUrl = getStoreUrl(platform);

  useEffect(() => {
    const knownScreen = screen && DEEP_LINK_ROUTE_MAP[screen] !== undefined;

    if (!knownScreen && !screen) {
      // Malformed link — no screen param at all, fall back to home (AC5)
      window.location.pathname = '/home';
      return;
    }

    if (platform === 'other') {
      // Desktop or unknown — show store buttons immediately
      setShowStoreFallback(true);
      return;
    }

    const appUrl = buildAppUrl(screen, id);

    let timer: ReturnType<typeof setTimeout>;
    let appOpened = false;

    const cancelFallback = (): void => {
      appOpened = true;
      clearTimeout(timer);
    };

    // Three signals that the OS handed off to the native app — any one is enough.
    // visibilitychange: reliable on most Android browsers.
    // pagehide: fires on iOS Safari when the page is pushed into the background.
    // blur: catches cases where the window loses focus without a visibility change.
    const handleVisibilityChange = (): void => { if (document.hidden) cancelFallback(); };
    const handlePageHide = (): void => cancelFallback();
    const handleBlur = (): void => cancelFallback();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('blur', handleBlur);

    timer = setTimeout(() => {
      if (!appOpened) {
        window.location.href = storeUrl ?? '/home';
      }
    }, STORE_FALLBACK_DELAY_MS);

    window.location.href = appUrl;

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('blur', handleBlur);
    };
  }, [screen, id, platform, storeUrl]);

  if (showStoreFallback) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100vh" gap={3}>
        <Typography variant="h6">Get the Peazy app</Typography>
        <Box display="flex" gap={2}>
          <Button variant="contained" href={PEAZY_IOS_STORE_URL} target="_blank" rel="noopener noreferrer">
            Download on the App Store
          </Button>
          <Button variant="outlined" href={PEAZY_ANDROID_STORE_URL} target="_blank" rel="noopener noreferrer">
            Get it on Google Play
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100vh" gap={2}>
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Opening Peazy…
      </Typography>
    </Box>
  );
};

export default OpenDeepLink;
